import {
  ClipboardList,
  FileText,
  Image as ImageIcon,
  Link as LinkIcon,
  Plus,
  X,
  type LucideIcon,
} from 'lucide-react-native';
import { useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { ChatComposerInput } from '@/components/composer/ChatComposerInput';
import { ConfirmationCheckbox } from '@/components/form/ConfirmationCheckbox';
import { GlassIconButton, GlassSurface, useLiquidGlass } from '@/components/ui/glass';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';

/** The server's limit on a native message's length. */
export const SUPPORT_MESSAGE_MAX_LENGTH = 5000;

/** The visual size of the chip's remove control, widened to the minimum target by hit slop. */
const REMOVE_ATTACHMENT_SIZE = 24;

export type ComposerAttachment = {
  /** The attached inquiry's title, as the reader would recognize it. */
  title: string;
};

/** A link the next message will carry, named by its page's title once that has unfurled. */
export type ComposerLink = {
  url: string;
  title?: string;
};

/** A file the next message will carry, as its chip names it. */
export type ComposerFile = {
  id: string;
  name: string;
  isImage: boolean;
};

type SupportThreadComposerProps = {
  onSend: (body: string) => void;
  /** Opens the attach menu. The attach button shows only when this is given. */
  onAttach?: () => void;
  /** The inquiry the next message will carry, shown as a removable chip above the text. */
  attachment?: ComposerAttachment;
  onRemoveAttachment?: () => void;
  /** The survey the next message will share, by name, as a removable chip above the text. */
  survey?: { name: string };
  onRemoveSurvey?: () => void;
  /** Files the next message will carry, each a removable chip above the text. */
  files?: ComposerFile[];
  onRemoveFile?: (id: string) => void;
  /** Links the next message will carry, each a removable chip above the text. */
  links?: ComposerLink[];
  onRemoveLink?: (index: number) => void;
  /** Whether the author confirmed the files carry no PHI; a send with files waits for it. */
  noPHIConfirmed?: boolean;
  onNoPHIConfirmedChange?: (isConfirmed: boolean) => void;
  /** Text the box opens with, such as a draft from the screen that sent the member here. */
  initialText?: string;
  /** Called as the text changes, so the caller can keep an unsent draft across remounts. */
  onTextChange?: (text: string) => void;
};

/**
 * The support thread's composer. A send clears the box at once and the message
 * appears in the thread as pending; a failure stays in the thread with Retry
 * rather than returning to the box, so nothing typed is lost either way.
 *
 * Unlike Research, the keyboard stays open after a send, since a conversation
 * often continues.
 *
 * The glass plus at the start of the box's action row, opposite Send as in
 * Claude's composer, opens a menu of what can be attached; each pick shows as a
 * chip inside the box. The caller owns the attachments and clears them when a
 * message is handed over, so a failed send keeps them on the failed bubble
 * rather than also in the box.
 *
 * Links ride in the message text, one per line after what was typed, so the
 * caller folds them into the body at send. A message with only links or files
 * may have no typed text.
 *
 * A message with files may have no text, as in any messenger, but cannot be
 * sent until the author confirms the files carry no PHI, the same
 * confirmation the inquiry chat asks for.
 */
export function SupportThreadComposer({
  onSend,
  onAttach,
  attachment,
  onRemoveAttachment,
  survey,
  onRemoveSurvey,
  files = [],
  onRemoveFile,
  links = [],
  onRemoveLink,
  noPHIConfirmed = false,
  onNoPHIConfirmedChange,
  initialText = '',
  onTextChange,
}: SupportThreadComposerProps) {
  const [text, setTextState] = useState(initialText);

  const setText = (next: string) => {
    setTextState(next);

    onTextChange?.(next);
  };

  const hasGlass = useLiquidGlass();

  const trimmed = text.trim();

  const hasFiles = files.length > 0;

  const needsConfirmation = hasFiles && !noPHIConfirmed;

  const hasLinks = links.length > 0;

  const canSubmit = (trimmed.length > 0 || hasFiles || hasLinks) && !needsConfirmation;

  const submit = () => {
    if (!canSubmit) {
      return;
    }

    onSend(trimmed);

    setText('');
  };

  const hasChips = Boolean(attachment) || Boolean(survey) || hasFiles || hasLinks;

  return (
    <View className="mx-auto w-full max-w-3xl px-4">
      <View className={twJoin('w-full', hasGlass && 'rounded-[28px] shadow-soft-2')}>
        <GlassSurface isInteractive className={twJoin('w-full', hasGlass && 'rounded-[28px]')}>
          {hasChips ? (
            <HStack className="flex-wrap gap-2 px-3 pt-3">
              {attachment ? (
                <AttachmentChip
                  icon={FileText}
                  title={attachment.title}
                  label={`Attached inquiry: ${attachment.title}`}
                  removeLabel="Remove attached inquiry"
                  onRemove={onRemoveAttachment}
                />
              ) : null}

              {survey ? (
                <AttachmentChip
                  icon={ClipboardList}
                  title={survey.name}
                  label={`Shared survey: ${survey.name}`}
                  removeLabel="Remove shared survey"
                  onRemove={onRemoveSurvey}
                />
              ) : null}

              {files.map((file) => (
                <AttachmentChip
                  key={file.id}
                  icon={file.isImage ? ImageIcon : FileText}
                  title={file.name}
                  label={`Attached ${file.isImage ? 'photo' : 'file'}: ${file.name}`}
                  removeLabel={`Remove ${file.name}`}
                  onRemove={onRemoveFile ? () => onRemoveFile(file.id) : undefined}
                />
              ))}

              {links.map((link, index) => (
                <AttachmentChip
                  key={`${index}-${link.url}`}
                  icon={LinkIcon}
                  title={link.title ?? link.url}
                  label={`Attached link: ${link.title ?? link.url}`}
                  removeLabel={`Remove link ${link.title ?? link.url}`}
                  onRemove={onRemoveLink ? () => onRemoveLink(index) : undefined}
                />
              ))}
            </HStack>
          ) : null}

          {hasFiles && onNoPHIConfirmedChange ? (
            <View className="px-4 pt-3">
              <ConfirmationCheckbox
                variant="phi"
                isChecked={noPHIConfirmed}
                onChange={onNoPHIConfirmedChange}
              />
            </View>
          ) : null}

          <ChatComposerInput
            value={text}
            onChangeText={setText}
            onSubmit={submit}
            canSubmit={canSubmit}
            placeholder="Write a message"
            maxLength={SUPPORT_MESSAGE_MAX_LENGTH}
            nativeID="supportThreadComposer"
            inputLabel="Message"
            sendLabel="Send message"
            sendHint={sendHintFor({ canSubmit, needsConfirmation })}
            refocusAfterSubmit
            leadingAction={
              onAttach ? (
                <GlassIconButton
                  icon={Plus}
                  accessibilityLabel="Add an attachment"
                  accessibilityHint="Choose a photo, a file or a link to send with your message"
                  onPress={onAttach}
                  fallbackClassName="border border-outline-500 bg-background-0 web:hover:bg-background-100"
                />
              ) : undefined
            }
          />
        </GlassSurface>
      </View>
    </View>
  );
}

function sendHintFor({
  canSubmit,
  needsConfirmation,
}: {
  canSubmit: boolean;
  needsConfirmation: boolean;
}): string | undefined {
  if (canSubmit) {
    return undefined;
  }

  return needsConfirmation
    ? 'Confirm the attachments contain no protected health information first'
    : 'Type a message first';
}

function AttachmentChip({
  icon,
  title,
  label,
  removeLabel,
  onRemove,
}: {
  icon: LucideIcon;
  title: string;
  label: string;
  removeLabel: string;
  onRemove?: () => void;
}) {
  return (
    <HStack className="min-w-0 max-w-full items-center gap-2 rounded-full bg-background-100 py-1 pl-3 pr-1">
      <Icon as={icon} className="h-4 w-4 text-typography-700" />

      <Text
        className="min-w-0 shrink text-sm text-typography-700"
        numberOfLines={1}
        accessibilityLabel={label}>
        {title}
      </Text>

      {onRemove ? (
        <Pressable
          onPress={onRemove}
          hitSlop={getAccessibleHitSlop(REMOVE_ATTACHMENT_SIZE)}
          accessibilityRole="button"
          accessibilityLabel={removeLabel}
          className="h-6 w-6 items-center justify-center rounded-full web:cursor-pointer data-[hover=true]:bg-background-200">
          <Icon as={X} className="h-4 w-4 text-typography-700" />
        </Pressable>
      ) : null}
    </HStack>
  );
}
